import type { ReactNode } from 'react';
import { QueryClient, QueryErrorResetBoundary } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { ConfigProvider, App } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import AuthBootstrap from '../components/auth/AuthBootstrap';
import { RbacProvider } from '../contexts/RbacContext';
import { UIConfigProvider } from '../contexts/UIConfigContext';
import { WebSocketProvider } from './WebSocketProvider';
import { TrackingProvider } from '../components/handsontable/tracking';
import { GlobalErrorFallback } from '../components/common/GlobalErrorFallback';
import { MaintenanceOverlay } from '../components/common/MaintenanceOverlay';
import { useTheme } from '../hooks/useTheme';
import {
  localStoragePersister,
  shouldDehydrateQuery,
  CACHE_BUSTER,
  CACHE_MAX_AGE_MS,
} from '../services/core/queryPersister';

interface AppProvidersProps {
  children: ReactNode;
  msalInstance: PublicClientApplication;
  queryClient: QueryClient;
}

function ThemedApp({ children }: { children: ReactNode }) {
  const { antdThemeConfig } = useTheme();

  return (
    <ConfigProvider locale={ruRU} theme={antdThemeConfig}>
      <App>
        <TrackingProvider>
          {children}
        </TrackingProvider>
      </App>
    </ConfigProvider>
  );
}

/**
 * Композитный провайдер для всего приложения
 *
 * Порядок провайдеров важен:
 * 1. PersistQueryClientProvider - React Query + восстановление кэша из localStorage
 * 2. QueryErrorResetBoundary + ErrorBoundary - глобальная обработка ошибок
 * 3. MsalProvider - Azure AD аутентификация
 * 4. AuthBootstrap - инициализация токена
 * 5. RbacProvider - система ролей и прав
 * 6. WebSocketProvider - WebSocket соединения
 * 7. MaintenanceOverlay - заглушка при техработах (внутри WS+RBAC, чтобы слушать события и проверять роли)
 * 8. ConfigProvider (Ant Design) - локализация UI
 * 9. App (Ant Design) - контекст для useApp() (message, notification, modal)
 * 10. TrackingProvider - отслеживание изменений
 *
 * Персистируются только feature-flags и rbac — данные, которые блокируют
 * первый рендер и редко меняются. Бизнес-данные всегда загружаются свежими.
 */
export function AppProviders({
  children,
  msalInstance,
  queryClient,
}: AppProvidersProps) {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: localStoragePersister,
        maxAge: CACHE_MAX_AGE_MS,
        buster: CACHE_BUSTER,
        dehydrateOptions: {
          shouldDehydrateQuery,
        },
      }}
    >
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      <QueryErrorResetBoundary>
        {({ reset }) => (
          <ErrorBoundary
            onReset={reset}
            fallbackRender={({ error, resetErrorBoundary }: FallbackProps) => (
              <GlobalErrorFallback 
                error={error} 
                onReset={resetErrorBoundary} 
              />
            )}
          >
            <MsalProvider instance={msalInstance}>
              <AuthBootstrap>
                <RbacProvider>
                  <UIConfigProvider>
                    <WebSocketProvider
                      autoConnect={true}
                      showNotifications="all"
                    >
                      <MaintenanceOverlay>
                        <ThemedApp>
                          {children}
                        </ThemedApp>
                      </MaintenanceOverlay>
                    </WebSocketProvider>
                  </UIConfigProvider>
                </RbacProvider>
              </AuthBootstrap>
            </MsalProvider>
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>
    </PersistQueryClientProvider>
  );
}
